import { Box, Button, DrawerBody, Flex, FormatNumber, HStack, IconButton, Input, Separator, Stack, Switch, Table, Tag, Text } from "@chakra-ui/react";
import { useQueryClient } from "@tanstack/react-query";
import { Form, UI } from "components";
import { Styles } from "config";
import { Mask } from "config";
import { API } from "config";
import { toaster } from "config";
import useCart from "hooks/useCart";
import { useData } from "hooks/useData";
import { useEffect, useState } from "react";
import Moment from "react-moment";
import { useNavigate } from "react-router";
import useOptions from "../../hooks/useOptions";

export default function Cart({ ...props }){

    const cart = useCart()
    const { cart:cartData, setCart } = useData()
    const options = useOptions();

    useEffect(() => {
        setCart({ step: 1, shipping_type: 2 })
    }, [cart.data?.id])

    return(
        <UI.Drawer size="md" title={<><UI.Icon name="cart" /> Sacola</>} {...props}>
            <DrawerBody>
                <UI.Alert size="sm" mb={4}>
                    Seu carrinho expira <b><Moment fromNow children={cart.data?.cart_expire_at} /></b>. Finalize o pedido antes para não perder a reserva dos itens.
                </UI.Alert>

                { cartData.step === 2 ? <Finish /> : <General {...props} /> }
            </DrawerBody>
        </UI.Drawer>
    )
}

const General = (props) => {

    const cart = useCart()
    const { setCart } = useData()
    const itens = cart.data?.itens ?? []
    const distributor = cart.data?.distributor_from ?? {}
    const [ loading, setLoading ] = useState()

    const countItens = () => {
        let total = 0
        itens.map(item => { total = total + item.quantity })
        return total
    }

    const handleRemove = async (item) => {
        setLoading(item?.id)

        try{
            await API.put('/store/cart/item/remove', { product_id: item.id })
            toaster.create({ title: 'Item removido', type: 'info' })
            cart.refetch()
        }catch(err){
            toaster.create({ title: 'Não foi possível remover', description: err?.response?.data?.message, type: 'warning' })
        }

        setLoading(false)
    }

    return(
        <Stack gap={4}>

            <Text color="GrayText">Itens do seu pedido:</Text>

            <Table.Root size="sm" variant="line">
                <Table.Header>
                    <Table.Row>
                        <Table.ColumnHeader>Produto</Table.ColumnHeader>
                        <Table.ColumnHeader>Und.</Table.ColumnHeader>
                        <Table.ColumnHeader>Subtotal</Table.ColumnHeader>
                        <Table.ColumnHeader></Table.ColumnHeader>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {itens.map(item => {

                        return(
                            <Table.Row key={item.id}>
                                <Table.Cell fontSize="xs"><Tag.Root colorPalette="orange"><Tag.Label>{item.quantity}</Tag.Label></Tag.Root> {item.product?.name}</Table.Cell>
                                <Table.Cell fontSize="xs"><FormatNumber value={item.product.consultant_price / 100} style="currency" currency="BRL" /></Table.Cell>
                                <Table.Cell><FormatNumber value={item.amount / 100} style="currency" currency="BRL" /></Table.Cell>
                                <Table.Cell><IconButton onClick={() => handleRemove(item?.product)} disabled={!!loading || cart.isFetching} loading={loading===item?.product?.id} size="xs" colorPalette="red" variant="outline"><UI.Icon name="trash" /></IconButton></Table.Cell>
                            </Table.Row>
                        )
                    })}
                </Table.Body>
            </Table.Root>

            <Flex justifyContent="space-between">
                <Box>
                    <Text fontWeight="bold">{countItens()} Itens na sacola</Text>
                    <Text color="orange.fg"><UI.Number.Number value={cart.data?.points} /> pontos</Text>
                </Box>

                <Box>
                    <Text fontSize="xs" color="GrayText">Total do pedido:</Text>
                    <Text fontSize="lg" fontWeight="bold" color="green.fg"><FormatNumber value={cart.data?.total_amount / 100} style="currency" currency="BRL" /></Text>
                </Box>
            </Flex>

            <Separator />

            <Box>
                <Text color="GrayText">Núcleo de distribuição selecionado:</Text>
                <Text fontWeight="bold" color="orange.fg" mt={2}>{distributor?.name}</Text>
                <Text color="GrayText" fontSize="xs" textTransform="capitalize">{distributor.city?.name}, {distributor.city?.state_code} - {distributor.address_neighborhood}</Text>
            </Box>

            <Separator />

            <Button colorPalette="orange" disabled={cart.data?.total_amount <= 0} onClick={() => setCart({ step: 2 })}><UI.Icon name="cart" /> Finalizar pedido <UI.Icon name="right" /></Button>
            <Button colorPalette="gray" variant="ghost" onClick={props.onClose} size="sm"><UI.Icon name="prev" /> Continuar comprando</Button>

        </Stack>
    )
}

const Finish = () => {

    const navigate = useNavigate()
    const queryClient = useQueryClient()
    const cart = useCart()
    const options = useOptions()
    const [ loading, setLoading ] = useState(false)
    const { cart:cartData,  setCart } = useData()

    // Sistema original: limite individual de Terceiros
    const isLimitEnabled = options.data?.isThirdPartyLimitEnabled;
    const limitPercent = options.data?.thirdPartyPercentage || 50;

    // Sistema novo: limites combinados de Bônus+Terceiros
    const bonusThirdMaxPercentage = options.data?.bonusThirdMaxPercentage ?? 0;
    const bonusThirdMaxFixedValue = options.data?.bonusThirdMaxFixedValue ?? 0;

    const filterPaymentMethods = (shippingType, currentMethods = []) => {
        if (shippingType === 1) {
            return currentMethods.filter(item => item.method !== 2)
        }
        return currentMethods
    }

    const getTotalPayments = () => {
        let total = 0
        cartData.payment_methods?.map(item => {
            const val = parseInt(Mask.digit(item.value))
            total = total + (isNaN(val) ? 0 : val)
        })
        return total
    }

    const getRestant = () => parseInt(cart.data?.total_amount - getTotalPayments())

    // Helpers para o sistema original (Terceiros)
    const hasThirdValue = () => {
        const third = cartData.payment_methods?.find(p => p.method === 7)
        return (parseInt(Mask.digit(third?.value)) || 0) > 0
    }

    // Helpers para o sistema novo (Bônus+Terceiros combinados)
    const getBonusThirdTotal = () =>
        (cartData.payment_methods ?? [])
            .filter(p => [1, 7].includes(p.method))
            .reduce((sum, p) => sum + (parseInt(Mask.digit(p.value)) || 0), 0);

    const hasBonusThirdValue = () => getBonusThirdTotal() > 0;

    // Retorna o teto efetivo (centavos) aplicando a regra mais restritiva entre % e fixo.
    // Retorna null quando nenhuma regra está ativa.
    const getBonusThirdLimit = () => {
        const total = cart.data?.total_amount || 0;
        const limits = [];
        if (bonusThirdMaxPercentage > 0) limits.push(Math.floor(total * (bonusThirdMaxPercentage / 100)));
        if (bonusThirdMaxFixedValue > 0) limits.push(bonusThirdMaxFixedValue);
        return limits.length > 0 ? Math.min(...limits) : null;
    };

    const handleSubmit = async () => {
        if(!window.confirm('Tem certeza que deseja finalizar o pedido?')) return

        const payment_methods = cartData.payment_methods
            ?.filter(item => Mask.digit(item.value) > 0)
            ?.map(item => ({
                method: item.method,
                amount: parseInt(Mask.digit(item.value))
            })) || [];

        // Guard 1: limite individual de Terceiros (sistema original)
        const THIRD_PARTY_ID = 7;
        const totalOrder = cart.data?.total_amount || 0;
        const thirdParty = payment_methods.find(p => p.method === THIRD_PARTY_ID);

        if (isLimitEnabled && thirdParty && thirdParty.amount > totalOrder * (limitPercent / 100)) {
            return toaster.create({
                title: 'Valor não permitido',
                description: `O pagamento por Terceiros não pode exceder ${limitPercent}% do total.`,
                type: 'warning'
            });
        }

        // Guard 2: limites combinados de Bônus+Terceiros (sistema novo)
        const bonusThirdTotal = payment_methods
            .filter(p => [1, 7].includes(p.method))
            .reduce((sum, p) => sum + p.amount, 0);

        if (bonusThirdTotal > 0) {
            const limit = getBonusThirdLimit();
            if (limit !== null && bonusThirdTotal > limit) {
                const limitFormatted = (limit / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
                return toaster.create({
                    title: 'Valor não permitido',
                    description: `Bônus e Terceiros combinados não podem exceder ${limitFormatted}.`,
                    type: 'warning'
                });
            }
        }

        setLoading(true)

        try {
            await API.put('/store/cart/shipping', { shipping_type: cartData.shipping_type })
            await API.put('/store/cart/payment', { payment_methods })
            await API.put('/store/cart/finish')

            toaster.create({ title: 'O pedido foi finalizado.', type: 'success' })
            navigate(`/pedidos/${cart.data?.cod}`)
            await cart.refetch()

            setCart({ payment_method: [], step: 1 })
            await queryClient.invalidateQueries('orders')
        } catch(err) {
            toaster.create({
                title: 'Não foi possível finalizar o pedido. Tente novamente.',
                description: err?.response?.data?.message,
                type: 'warning'
            })
        }

        setLoading(false)
    }

    useEffect(() => {
        const filteredMethods = filterPaymentMethods(cartData.shipping_type, cartData.payment_methods)
        if (JSON.stringify(filteredMethods) !== JSON.stringify(cartData.payment_methods)) {
            setCart({ payment_methods: filteredMethods })
        }
    }, [cartData.shipping_type])

    return(
        <Stack gap={4}>

            <Box>
                <Form.Segment
                    required
                    label="Forma de entrega:"
                    items={[
                        { label: <HStack><UI.Icon name="withdraw" /> Retirada</HStack>, value: 2 },
                        { label: <HStack><UI.Icon name="shipping" /> Entrega</HStack>, value: 1 }
                    ]}
                    defaultValue={cartData?.shipping_type}
                    onValueChange={e => setCart({
                        shipping_type: e.value,
                        payment_methods: filterPaymentMethods(e.value, cartData.payment_methods)
                    })}
                    disabled={loading}
                />
            </Box>

            <Separator />

            <Box>
                <Text color="GrayText">Formas de pagamento:</Text>

                <UI.Show visible={getTotalPayments() > cart.data?.total_amount}>
                    <UI.Alert status="warning" mt={4} size="sm">
                        O valor total dos pagamentos ultrapassou o valor do pedido. Por favor, ajuste os valores.
                    </UI.Alert>
                </UI.Show>

                {/* Alerta original: limite percentual de Terceiros */}
                <UI.Show visible={isLimitEnabled && hasThirdValue()}>
                    <UI.Alert status="warning" mt={4} size="sm">
                        Pagamento por Terceiros limitado a {limitPercent}% do total do pedido.
                    </UI.Alert>
                </UI.Show>

                {/* Alerta novo: limites combinados de Bônus+Terceiros */}
                <UI.Show visible={hasBonusThirdValue() && (bonusThirdMaxPercentage > 0 || bonusThirdMaxFixedValue > 0)}>
                    <UI.Alert status="warning" mt={4} size="sm">
                        {(() => {
                            const limit = getBonusThirdLimit();
                            if (limit === null) return null;
                            const fmt = v => (v / 100).toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' });
                            const parts = [];
                            if (bonusThirdMaxPercentage > 0) parts.push(`${bonusThirdMaxPercentage}% do total`);
                            if (bonusThirdMaxFixedValue > 0) parts.push(fmt(bonusThirdMaxFixedValue));
                            return `Bônus e Terceiros limitados a ${parts.join(' ou ')} por pedido (máximo: ${fmt(limit)}).`;
                        })()}
                    </UI.Alert>
                </UI.Show>

                <Text fontSize="sm" color="GrayText" mt={4}>Valor restante:</Text>
                <Text fontSize="lg" color="orange.fg" fontWeight="bold"><FormatNumber value={getRestant()/100} style="currency" currency="BRL" /></Text>

                <Stack mt={4}>
                    { cart.data?.available_payment_methods
                        ?.filter(method => {
                            if (cartData.shipping_type === 1) {
                                return method.value !== 2
                            }
                            return true
                        })
                        ?.map(item => <PaymentMethodWidget key={item.method} data={item} loading={loading} />)
                    }
                </Stack>
            </Box>

            <Stack>
                <Button size="lg" colorPalette="orange" onClick={handleSubmit} loading={loading} loadingText="Finalizando pedido..." disabled={getTotalPayments() !== cart.data?.total_amount}><UI.Icon name="check" /> Finalizar pedido <UI.Icon name="next" /></Button>
                <Button disabled={loading} size="xs" variant="ghost" onClick={() => setCart({ step: 1 })}><UI.Icon name="prev" /> Voltar para a sacola <UI.Icon name="cart" /></Button>
            </Stack>
        </Stack>
    )
}

const PaymentMethodWidget = ({ data, loading = false, ...props }) => {
    const { cart: cartData, setCart } = useData();
    const cart = useCart();
    const options = useOptions();

    const payment = cartData?.payment_methods?.filter(item => item.method === data.value)?.[0];
    const int_value = payment?.value ? parseInt(Mask.digit(payment?.value)) : 0;
    const style = Styles.transactionMethod(data.value);
    const color = int_value >= 1 ? `${style.colorPalette}.fg` : 'GrayText';

    // Sistema original: limite individual de Terceiros
    const IS_THIRD_PARTY = data.value === 7;
    const isLimitEnabled = options.data?.isThirdPartyLimitEnabled;
    const limitPercent = options.data?.thirdPartyPercentage || 50;

    // Sistema novo: limites combinados de Bônus+Terceiros
    const IS_BONUS_OR_THIRD = [1, 7].includes(data.value);
    const bonusThirdMaxPercentage = options.data?.bonusThirdMaxPercentage ?? 0;
    const bonusThirdMaxFixedValue = options.data?.bonusThirdMaxFixedValue ?? 0;
    const hasAnyLimit = bonusThirdMaxPercentage > 0 || bonusThirdMaxFixedValue > 0;

    const handleChangePaymentMethod = (method, value) => {
        let cleanValue = Mask.digit(value) || "";

        // Trava original: bloqueia Terceiros acima do percentual individual
        if (isLimitEnabled && IS_THIRD_PARTY && cleanValue !== "") {
            const limit = (cart.data?.total_amount || 0) * (limitPercent / 100);
            if (parseInt(cleanValue) > limit) return;
        }

        // Trava nova: bloqueia Bônus+Terceiros combinados acima dos limites adicionais
        if (IS_BONUS_OR_THIRD && hasAnyLimit && cleanValue !== "") {
            const otherBonusThirdTotal = (cartData.payment_methods ?? [])
                .filter(p => [1, 7].includes(p.method) && p.method !== data.value)
                .reduce((sum, p) => sum + (parseInt(Mask.digit(p.value)) || 0), 0);
            const combined = parseInt(cleanValue) + otherBonusThirdTotal;
            const total = cart.data?.total_amount || 0;

            if (bonusThirdMaxPercentage > 0 && combined > Math.floor(total * (bonusThirdMaxPercentage / 100))) return;
            if (bonusThirdMaxFixedValue > 0 && combined > bonusThirdMaxFixedValue) return;
        }

        const payment_methods = [...cartData?.payment_methods ?? []];
        const payment = payment_methods.filter(item => item.method === method)?.[0];

        const masked_value = cleanValue === '' ? '' : `R$ ${Mask.price(cleanValue)}`;

        if(payment){
            const index = payment_methods.findIndex(item => item.method === method);
            payment_methods[index] = { method, value: masked_value };
        } else {
            payment_methods.push({ method, value: masked_value });
        }

        setCart({ payment_methods });
    };

    // Botão original: aplica o percentual individual no campo Terceiros
    const handleApplyLimitPercent = () => {
        const total = cart.data?.total_amount || 0;
        const maxAmount = Math.floor(total * (limitPercent / 100));

        const payment_methods = [...cartData?.payment_methods ?? []];
        const masked_value = `R$ ${Mask.price(maxAmount.toString())}`;
        const index = payment_methods.findIndex(item => item.method === data.value);

        if (index !== -1) payment_methods[index] = { method: data.value, value: masked_value };
        else payment_methods.push({ method: data.value, value: masked_value });

        setCart({ payment_methods });
    };

    // Botão novo: aplica o teto combinado de Bônus+Terceiros no campo atual
    const handleApplyLimit = () => {
        const total = cart.data?.total_amount || 0;
        const otherBonusThirdTotal = (cartData.payment_methods ?? [])
            .filter(p => [1, 7].includes(p.method) && p.method !== data.value)
            .reduce((sum, p) => sum + (parseInt(Mask.digit(p.value)) || 0), 0);

        const limits = [];
        if (bonusThirdMaxPercentage > 0) limits.push(Math.floor(total * (bonusThirdMaxPercentage / 100)));
        if (bonusThirdMaxFixedValue > 0) limits.push(bonusThirdMaxFixedValue);
        const maxAllowed = Math.max(0, Math.min(...limits) - otherBonusThirdTotal);

        const payment_methods = [...cartData?.payment_methods ?? []];
        const masked_value = `R$ ${Mask.price(maxAllowed.toString())}`;
        const index = payment_methods.findIndex(item => item.method === data.value);

        if (index !== -1) payment_methods[index] = { method: data.value, value: masked_value };
        else payment_methods.push({ method: data.value, value: masked_value });

        setCart({ payment_methods });
    };

    return (
        <Box
            borderWidth={1}
            py={2}
            px={4}
            borderRadius={4}
            borderStyle={int_value >= 1 ? 'solid' : 'dashed'}
            borderColor={int_value >= 1 ? 'orange' : null}
        >
            <Flex justifyContent="space-between" align="center">
                <HStack gap={3}>
                    <UI.Icon name={style.icon} color={color} />

                    <HStack gap={2}>
                        <Text fontWeight="bold" color={color}>{data.label}</Text>

                        {/* Botão original: aplica percentual no campo Terceiros */}
                        {IS_THIRD_PARTY && isLimitEnabled && (
                            <Button
                                variant="outline"
                                size="xs"
                                colorPalette="orange"
                                onClick={handleApplyLimitPercent}
                                height="24px"
                                fontSize="11px"
                                px={2}
                            >
                                <UI.Icon name="check" size="2xs" /> Aplicar {limitPercent}%
                            </Button>
                        )}

                        {/* Botão novo: aplica teto combinado no campo Bônus (ou Terceiros quando não há regra individual) */}
                        {IS_BONUS_OR_THIRD && hasAnyLimit && !(IS_THIRD_PARTY && isLimitEnabled) && (
                            <Button
                                variant="outline"
                                size="xs"
                                colorPalette="orange"
                                onClick={handleApplyLimit}
                                height="24px"
                                fontSize="11px"
                                px={2}
                            >
                                <UI.Icon name="check" size="2xs" />
                                {bonusThirdMaxPercentage > 0 ? `Aplicar ${bonusThirdMaxPercentage}%` : 'Aplicar máx.'}
                            </Button>
                        )}
                    </HStack>
                </HStack>

                <Box>
                    <Input
                        disabled={loading}
                        inputMode="numeric"
                        size="xs"
                        variant="subtle"
                        w="120px"
                        fontWeight="bold"
                        color={color}
                        placeholder="Ex.: R$ 100,00"
                        value={payment?.value || ''}
                        onChange={e => handleChangePaymentMethod(data.value, e.target.value)}
                    />
                    <IconButton
                        disabled={loading}
                        size="xs"
                        variant="ghost"
                        colorPalette={color}
                        onClick={() => handleChangePaymentMethod(data.value, '' )}
                    >
                        <UI.Icon name="cancel" />
                    </IconButton>
                </Box>
            </Flex>
        </Box>
    );
};